import { create } from 'zustand';
import type { GenerationProgress, GenerationSettings } from '../pages/GenerateTimetable/MockData';
import { defaultGenerationSettings } from '../pages/GenerateTimetable/MockData';
import type { GenerationRecord } from '../pages/GenerationHistory/MockData';
import { mockGenerationHistory } from '../pages/GenerationHistory/MockData';

interface GeneratorState {
  // Generation Settings
  settings: GenerationSettings;
  updateSettings: (settings: Partial<GenerationSettings>) => void;
  resetSettings: () => void;

  // Generation Progress
  progress: GenerationProgress;
  setProgress: (progress: Partial<GenerationProgress>) => void;
  resetProgress: () => void;

  // Generation History
  history: GenerationRecord[];
  addHistoryRecord: (record: GenerationRecord) => void;
  clearHistory: () => void;

  // Generation Control
  isGenerating: boolean;
  startGeneration: () => void;
  stopGeneration: () => void;
  completeGeneration: (timetableId: string) => void;
  failGeneration: (error: string) => void;
}

export const useGeneratorStore = create<GeneratorState>((set, _get) => ({
  // Initial Settings
  settings: defaultGenerationSettings,

  updateSettings: (newSettings) =>
    set((state) => ({
      settings: { ...state.settings, ...newSettings },
    })),

  resetSettings: () =>
    set(() => ({
      settings: defaultGenerationSettings,
    })),

  // Initial Progress
  progress: {
    status: 'idle',
    progress: 0,
    currentStep: 'Ready to generate',
    message: 'Click Generate Timetable to begin',
  },

  setProgress: (newProgress) =>
    set((state) => ({
      progress: { ...state.progress, ...newProgress },
    })),

  resetProgress: () =>
    set(() => ({
      progress: {
        status: 'idle',
        progress: 0,
        currentStep: 'Ready to generate',
        message: 'Click Generate Timetable to begin',
      },
      isGenerating: false,
    })),

  // Generation History
  history: mockGenerationHistory,

  addHistoryRecord: (record) =>
    set((state) => ({
      history: [record, ...state.history],
    })),

  clearHistory: () =>
    set(() => ({
      history: [],
    })),

  // Generation Control
  isGenerating: false,

  startGeneration: () =>
    set(() => ({
      isGenerating: true,
      progress: {
        status: 'preparing',
        progress: 0,
        currentStep: 'Initializing',
        message: 'Starting timetable generation...',
        startTime: new Date(),
      },
    })),

  stopGeneration: () =>
    set((state) => ({
      isGenerating: false,
      progress: {
        ...state.progress,
        status: 'idle',
        message: 'Generation stopped by user',
      },
    })),

  completeGeneration: (timetableId) =>
    set((state) => ({
      isGenerating: false,
      progress: {
        ...state.progress,
        status: 'complete',
        progress: 100,
        currentStep: 'Complete',
        message: 'Timetable generated successfully!',
        endTime: new Date(),
        timetableId,
      },
    })),

  failGeneration: (error) =>
    set((state) => ({
      isGenerating: false,
      progress: {
        ...state.progress,
        status: 'error',
        message: error,
        endTime: new Date(),
      },
    })),
}));
